// src/contexts/TechnicalNotesContext.tsx
// Le note tecniche sono poche righe per cliente ma servono in tre punti diversi
// (scheda cliente, dettaglio appuntamento, icona in agenda): si caricano una volta
// per shop e si tengono qui.
import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useMemo,
    ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import {
    fetchShopTechnicalNotes,
    saveTechnicalNote,
    deleteTechnicalNote,
    TechnicalNote,
} from '@/services/clients/technicalNotesService';

interface TechnicalNotesContextValue {
    notes: TechnicalNote[];
    loading: boolean;
    /** true se il titolare ha riservato le note a sé e chi guarda non è admin */
    isHidden: boolean;
    getNotesForClient: (clientId?: string | null) => TechnicalNote[];
    hasNotes: (clientId?: string | null) => boolean;
    clientIdsWithNotes: Set<string>;
    saveNoteForClient: (
        clientId: string,
        content: string
    ) => Promise<{ success: boolean; error?: string }>;
    removeNote: (id: number) => Promise<{ success: boolean; error?: string }>;
    refresh: () => Promise<void>;
}

const TechnicalNotesContext = createContext<TechnicalNotesContextValue | null>(null);

export const useTechnicalNotes = () => {
    const context = useContext(TechnicalNotesContext);
    if (!context) {
        throw new Error('useTechnicalNotes must be used within a TechnicalNotesProvider');
    }
    return context;
};

const todayISO = () => {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const TechnicalNotesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { organization, isAuthenticated, user, isAdmin } = useAuth();
    const [notes, setNotes] = useState<TechnicalNote[]>([]);
    const [loading, setLoading] = useState(false);

    // Privacy: se il titolare ha scelto "solo io", i collaboratori non le vedono
    // (le policy sul database fanno comunque da rete di sicurezza).
    const isHidden =
        organization?.technical_notes_visibility === 'owner_only' && !isAdmin;

    const refresh = useCallback(async () => {
        if (!organization?.id || isHidden) {
            setNotes([]);
            return;
        }
        setLoading(true);
        const res = await fetchShopTechnicalNotes(organization.id);
        if (res.success) setNotes(res.data);
        setLoading(false);
    }, [organization?.id, isHidden]);

    useEffect(() => {
        if (isAuthenticated && organization?.id) refresh();
    }, [isAuthenticated, organization?.id, refresh]);

    const clientIdsWithNotes = useMemo(() => {
        const set = new Set<string>();
        notes.forEach((n) => {
            if (n.content && n.content.trim()) set.add(String(n.client_id));
        });
        return set;
    }, [notes]);

    const getNotesForClient = useCallback(
        (clientId?: string | null) => {
            if (!clientId) return [];
            return notes.filter((n) => String(n.client_id) === String(clientId));
        },
        [notes]
    );

    const hasNotes = useCallback(
        (clientId?: string | null) => (clientId ? clientIdsWithNotes.has(String(clientId)) : false),
        [clientIdsWithNotes]
    );

    const saveNoteForClient = useCallback(
        async (clientId: string, content: string) => {
            if (!organization?.id) return { success: false, error: 'Nessun salone attivo' };

            const note_date = todayISO();
            const author_id = user?.id ?? null;
            const res = await saveTechnicalNote({
                shop_id: Number(organization.id),
                client_id: clientId,
                content,
                note_date,
                author_id,
                author_name:
                    [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
                    user?.email ||
                    null,
            });

            if (res.success && res.data) {
                const saved = res.data;
                setNotes((prev) => {
                    const rest = prev.filter((n) => n.id !== saved.id);
                    return [saved, ...rest];
                });
            }
            return { success: res.success, error: res.error };
        },
        [organization?.id, user?.id, user?.first_name, user?.last_name, user?.email]
    );

    const removeNote = useCallback(async (id: number) => {
        const res = await deleteTechnicalNote(id);
        if (res.success) setNotes((prev) => prev.filter((n) => n.id !== id));
        return res;
    }, []);

    return (
        <TechnicalNotesContext.Provider
            value={{
                notes,
                loading,
                isHidden,
                getNotesForClient,
                hasNotes,
                clientIdsWithNotes,
                saveNoteForClient,
                removeNote,
                refresh,
            }}
        >
            {children}
        </TechnicalNotesContext.Provider>
    );
};
